import { useCallback, useEffect, useState } from "react";

import { type CrudFilter, useDataProvider } from "@refinedev/core";

import type { TinaSiteOption } from "@/pages/tina/runs/components/site-picker/utils";

// The backend caps `limit` at 200; "select all in country" loops pages up to
// this many (a single country's enabled sites stays well under the cap).
const SELECT_ALL_PAGE_SIZE = 200;
const MAX_SELECT_ALL_PAGES = 50;

export interface RunPickerScope {
  contentType: string;
  /** Backend `source_kind`: "web" or "instagram". */
  sourceKind: string;
  countryCode: string | null;
  city: string | null;
  /** Partial-match search against `name`. */
  query: string;
}

export interface UseRunPickerSitesArgs extends RunPickerScope {
  /** 1-based page. */
  page: number;
  pageSize: number;
}

export interface UseRunPickerSitesResult {
  rows: TinaSiteOption[];
  total: number;
  isLoading: boolean;
  error: string | null;
  /**
   * Fetch every enabled site in the current content × source × country scope —
   * ignoring the city + search refinements — paged. Returns the full records so
   * the caller can both select the names and populate the basket. Bounded by the
   * country's size.
   */
  fetchAllInScope: () => Promise<TinaSiteOption[]>;
}

function scopeFilters(
  scope: RunPickerScope,
  opts: { includeCity: boolean; includeQuery: boolean },
): CrudFilter[] {
  const filters: CrudFilter[] = [
    { field: "enabled", operator: "eq", value: true },
    { field: "content_type", operator: "eq", value: scope.contentType },
    { field: "source_kind", operator: "eq", value: scope.sourceKind },
  ];
  if (scope.countryCode) {
    filters.push({
      field: "country_code",
      operator: "eq",
      value: scope.countryCode,
    });
  }
  const city = scope.city?.trim();
  if (opts.includeCity && city) {
    filters.push({ field: "city", operator: "eq", value: city });
  }
  const query = scope.query.trim();
  if (opts.includeQuery && query) {
    filters.push({ field: "name", operator: "eq", value: query });
  }
  return filters;
}

/**
 * Server-paginated enabled-site listing for the run-create picker, scoped to the
 * active content type / source / country / city / search. Replaces the previous
 * load-every-site-into-memory hook; the picker faceting now runs server-side via
 * the `/sites` filters and `/sites/facets` endpoint.
 */
export function useRunPickerSites({
  page,
  pageSize,
  contentType,
  sourceKind,
  countryCode,
  city,
  query,
}: UseRunPickerSitesArgs): UseRunPickerSitesResult {
  const { getList } = useDataProvider()("tina");
  const [rows, setRows] = useState<TinaSiteOption[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    // No country chosen yet → nothing to list (matches the picker's gate).
    if (!countryCode) {
      setRows([]);
      setTotal(0);
      setError(null);
      setIsLoading(false);
      return;
    }

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getList?.({
          resource: "tina-sites",
          pagination: { currentPage: page, pageSize, mode: "server" },
          filters: scopeFilters(
            { contentType, sourceKind, countryCode, city, query },
            { includeCity: true, includeQuery: true },
          ),
          sorters: [],
        });
        if (isMounted) {
          setRows((response?.data ?? []) as TinaSiteOption[]);
          setTotal(response?.total ?? 0);
        }
      } catch (caught) {
        if (isMounted) {
          setError(
            caught instanceof Error ? caught.message : "Failed to load sites",
          );
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [
    getList,
    contentType,
    sourceKind,
    countryCode,
    city,
    query,
    page,
    pageSize,
  ]);

  const fetchAllInScope = useCallback(async () => {
    if (!countryCode) return [];
    const all: TinaSiteOption[] = [];
    for (let p = 1; p <= MAX_SELECT_ALL_PAGES; p += 1) {
      const response = await getList?.({
        resource: "tina-sites",
        pagination: {
          currentPage: p,
          pageSize: SELECT_ALL_PAGE_SIZE,
          mode: "server",
        },
        filters: scopeFilters(
          { contentType, sourceKind, countryCode, city, query },
          { includeCity: false, includeQuery: false },
        ),
        sorters: [],
      });
      const data = (response?.data ?? []) as TinaSiteOption[];
      all.push(...data);
      const totalCount = response?.total ?? data.length;
      if (data.length === 0 || all.length >= totalCount) break;
    }
    return all;
  }, [getList, contentType, sourceKind, countryCode, city, query]);

  return { rows, total, isLoading, error, fetchAllInScope };
}
